import { styled } from '@mui/material/styles';
import { ReactComponent as DeliveryAddressIcon } from '@Src/assets/images/location.svg';
import { ReactComponent as StoreAddressIcon } from '@Src/assets/images/store.svg';
import { ReactComponent as EditIcon } from '@Src/assets/images/edit.svg';
import { useTranslation } from '@Src/providers/TranslationProvider';
import LinkButton from '@Components/ui/LinkButton';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { StoreStatusIcon } from '../StoreStatusIcon';
import { TextField } from '@Components/ui/TextField';
import { ReactComponent as SearchIcon } from '@Src/assets/images/search.svg';
import { UseCurrentLocationButton } from '../EditOrderSettingsDialog/DeliverySettings/UseCurrentLocationButton';
import { type DeliveryFlowStepType } from '../EditOrderSettingsDialog/DeliverySettings';
import { useTracking } from '@Src/hooks/useTracking';
import { useAppSelector } from '@Src/hooks/useAppSelector';
import {
  type SupportedCountries,
  getPostCodeExample,
  getPostCodeLabel,
} from '@Src/utility/locationUtils';
import { useDesignSchema } from '@Src/providers/DesignSchemaProvider';

const StyledContentContainer = styled(Grid)<{
  ownerState: { isToggleHidden?: boolean };
}>(({ ownerState, theme }) => ({
  backgroundColor: theme.palette.background.default,
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: '0px 12px 36px 0px rgba(0, 0, 0, 0.15)',
  display: 'flex',
  justifyContent: 'space-between',
  marginTop: ownerState.isToggleHidden ? theme.spacing(-3) : theme.spacing(-4),
  paddingTop: ownerState.isToggleHidden ? theme.spacing(2) : theme.spacing(4),
  paddingRight: theme.spacing(2),
  paddingBottom: theme.spacing(2),
  paddingLeft: theme.spacing(3.5),
  [theme.breakpoints.down('sm')]: {
    boxShadow: 'none',
    flexDirection: 'column',
    marginTop: ownerState.isToggleHidden
      ? theme.spacing(-3.5)
      : theme.spacing(-4),
    paddingLeft: theme.spacing(2),
    width: '100%',
  },
}));

const StyledAddressEditContainer = styled('div')`
  display: flex;
  align-items: center;
  margin-right: ${({ theme }) => theme.spacing(1)};
  ${({ theme }) => theme.breakpoints.down('sm')} {
    width: 100%;
    justify-content: space-between;
    margin-right: 0;
  }
  &:hover {
    cursor: pointer;
  }
`;

const StyledEditIcon = styled(EditIcon)`
  margin-right: ${({ theme }) => theme.spacing(1)};
  height: 16px;
  width: 16px;
`;

const StyledEditButton = styled('button')`
  display: flex;
  align-items: center;
  text-decoration: underline;
  text-decoration-style: dashed;
  text-decoration-color: ${({ theme }) => theme.palette.primary.main};
  ${({ theme }) => theme.breakpoints.up('xs')} {
    margin-left: ${({ theme }) => theme.spacing(4)};
  }
  &:hover {
    cursor: pointer;
  }
`;

const SyledAddressContainer = styled('div')`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const StyledAddressTextContainer = styled('div')(({ theme }) => ({
  marginLeft: theme.spacing(0.5),
}));

const StyledOrderTimeText = styled(Typography)`
  color: ${({ theme }) => theme.palette.text.secondary};
`;

const StyledAddressText = styled(Typography)`
  color: ${({ theme }) => theme.palette.text.primary};
  margin: 0;
`;

const StyledStatusContainer = styled('div')(({ theme }) => ({
  alignItems: 'center',
  display: 'flex',
  flexDirection: 'row',
  minWidth: '110px',
  justifyContent: 'flex-end',
  marginRight: theme.spacing(3),
  [theme.breakpoints.down('sm')]: {
    justifyContent: 'center',
    marginTop: theme.spacing(2),
    marginRight: theme.spacing(0),
    width: '100%',
  },
}));

const StyledStatusAndOrderContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  width: '60%',
  [theme.breakpoints.down('md')]: {
    width: 'inherit',
  },
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column-reverse',
    marginTop: '16px',
    width: '100%',
  },
}));

const StyledStatusText = styled(Typography)(({ theme }) => ({
  color: theme.palette.secondary.body,
}));

const StyledSearch = styled(TextField)(({ theme }) => ({
  paddingTop: theme.spacing(1.5),
  width: '100%',
}));

const StyledSearchIcon = styled(SearchIcon)`
  color: ${({ theme }) => theme.palette.text.primary};
`;

const StyledLocationContainer = styled(Grid)(({ theme }) => ({
  paddingLeft: theme.spacing(1),
  [theme.breakpoints.down('sm')]: {
    justifyContent: 'center',
    paddingLeft: 0,
    marginTop: theme.spacing(1.5),
  },
}));

export type OrderSettingDetailsPropsType = {
  collectionStoresLength: number;
  deliveryStoresLength: number;
  hasUserDeliveryAddress: boolean;
  hasSelectedDeliveryStore: boolean;
  locationName: string;
  orderSetting: 'Deliver' | 'Collect';
  orderUrl?: string;
  setIsDialogOpen: React.Dispatch<
    React.SetStateAction<{
      isOpen: boolean;
      startStep?: DeliveryFlowStepType;
    }>
  >;
  statusMessage: string;
  storeAcceptsPreorders: boolean;
  storeOpen: boolean;
};

export function OrderSettingDetails({
  collectionStoresLength,
  deliveryStoresLength,
  hasUserDeliveryAddress,
  hasSelectedDeliveryStore,
  locationName,
  orderSetting,
  orderUrl = '',
  setIsDialogOpen,
  statusMessage,
  storeAcceptsPreorders,
  storeOpen,
}: OrderSettingDetailsPropsType): JSX.Element {
  const { translate } = useTranslation();
  const { config } = useDesignSchema('v2');
  const { trackOrderEvent, trackNavigationEvent } = useTracking();

  const userCountryCode = useAppSelector(
    (state) => state.location.ipInfo?.country,
  ) as SupportedCountries;

  const showEditButton =
    !!(orderSetting === 'Collect' && collectionStoresLength > 1) ||
    orderSetting === 'Deliver';

  const handleEditClick = (): void => {
    trackNavigationEvent('Edit Order Settings', {
      orderType: orderSetting === 'Collect' ? 'Collection' : 'Delivery',
    });
    setIsDialogOpen({
      isOpen: true,
    });
  };

  const handleViewMenuClick = (): void => {
    trackOrderEvent('Start Order', {
      orderType: orderSetting === 'Collect' ? 'Collection' : 'Delivery',
      section: 'Order Settings',
    });
  };

  const LocationIcon =
    orderSetting === 'Deliver' ? (
      <DeliveryAddressIcon data-fd="delivery-address-icon" />
    ) : (
      <StoreAddressIcon data-fd="store-address-icon" />
    );

  if (
    orderSetting === 'Deliver' &&
    (!hasUserDeliveryAddress || !hasSelectedDeliveryStore)
  ) {
    return (
      <StyledContentContainer
        container
        ownerState={{
          isToggleHidden: !collectionStoresLength || !deliveryStoresLength,
        }}
      >
        <Grid item xs={12} sm={7}>
          <StyledSearch
            endIcon={<StyledSearchIcon data-fd="search-icon" />}
            label={translate(
              getPostCodeLabel(
                (config?.countryCode as SupportedCountries) || userCountryCode,
              ),
            )}
            onClick={() =>
              setIsDialogOpen({
                isOpen: true,
                startStep: 'addressSearch',
              })
            }
            placeholder={
              userCountryCode &&
              `${translate('e.g.')} ${getPostCodeExample(
                (config?.countryCode as SupportedCountries) || userCountryCode,
              )}`
            }
          />
        </Grid>
        <StyledLocationContainer item display="flex" alignItems="flex-end">
          <UseCurrentLocationButton
            onLocationRetrieved={() =>
              setIsDialogOpen({
                isOpen: true,
                startStep: 'addressConfirm',
              })
            }
          />
        </StyledLocationContainer>
      </StyledContentContainer>
    );
  } else {
    return (
      <StyledContentContainer
        ownerState={{
          isToggleHidden: !collectionStoresLength || !deliveryStoresLength,
        }}
      >
        <StyledAddressEditContainer onClick={handleEditClick}>
          <SyledAddressContainer>
            {LocationIcon}
            <StyledAddressTextContainer>
              <StyledOrderTimeText variant="finePrint1">
                {translate(orderSetting)} ASAP
              </StyledOrderTimeText>
              <StyledAddressText variant="body2Medium" paragraph>
                {locationName}
              </StyledAddressText>
            </StyledAddressTextContainer>
          </SyledAddressContainer>
          {showEditButton && (
            <StyledEditButton>
              <StyledEditIcon />
              <Typography variant="body2">{translate('Edit')}</Typography>
            </StyledEditButton>
          )}
        </StyledAddressEditContainer>

        <StyledStatusAndOrderContainer>
          <StyledStatusContainer>
            <StoreStatusIcon
              isOpen={storeOpen}
              acceptPreorders={storeAcceptsPreorders}
              size="12px"
            />
            <StyledStatusText variant="finePrint1">
              {statusMessage}
            </StyledStatusText>
          </StyledStatusContainer>
          <LinkButton
            onClick={handleViewMenuClick}
            label={translate('View_menu')}
            href={orderUrl}
          />
        </StyledStatusAndOrderContainer>
      </StyledContentContainer>
    );
  }
}
